import { IMdfActivity } from 'partner360/features/mdfActivities/model/types'

/**
 * The payload is inconsistent about money types — requested amounts arrive as
 * numeric strings (`"11749.500646223"`) while claim amounts arrive as numbers —
 * so every amount is coerced before use. `null`, `''` and junk become 0, which
 * keeps sums numeric instead of turning `+=` into string concatenation.
 */
const toAmount = (value: unknown): number => {
  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : 0
}

export const getRequested = (activity: IMdfActivity, isUsd: boolean): number =>
  toAmount(isUsd ? activity.requestedAmountUsd : activity.requestedAmountLc)

export const getPaid = (activity: IMdfActivity, isUsd: boolean): number =>
  toAmount(isUsd ? activity.claimPaidUsd : activity.claimPaidLc)

/**
 * FX rate implied by the activity's own amounts. The payload carries no explicit
 * rate, and the category split (`strategicInitiativeAllocations`) reports amounts
 * in local currency only, so allocated amounts are converted with the activity's
 * own `requestedAmountUsd / requestedAmountLc` ratio. Returns 0 when the activity
 * has no local amount to derive a rate from.
 */
export const getLcToUsdRate = (activity: IMdfActivity): number => {
  const requestedLc = getRequested(activity, false)

  return requestedLc ? getRequested(activity, true) / requestedLc : 0
}

export interface IGroupTotals {
  requested: number
  paid: number
}

export const sumActivities = (activities: IMdfActivity[], isUsd: boolean): IGroupTotals =>
  activities.reduce<IGroupTotals>(
    (acc, activity) => {
      acc.requested += getRequested(activity, isUsd)

      acc.paid += getPaid(activity, isUsd)

      return acc
    },
    { requested: 0, paid: 0 }
  )
